import { useQuery } from "@tanstack/react-query";
import {
  BadgeCheck,
  CheckCircle2,
  ExternalLink,
  FileText,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { formatDate, relativeTime } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Badge,
  Card,
  CardContent,
  Separator,
  Skeleton,
} from "@/components/ui/primitives";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/overlays";

export const DocumentViewer = ({
  requestId,
  open,
  onOpenChange,
  onApproveVerify,
  onApproveOnly,
  onReject,
}: {
  requestId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApproveVerify?: (id: string) => void;
  onApproveOnly?: (id: string) => void;
  onReject?: (id: string) => void;
}) => {
  const { data: vReq, isLoading, error } = useQuery({
    queryKey: ["verification", "detail", requestId],
    queryFn: () => api.get<any>(`/verification/${requestId}`),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>
            {isLoading ? "Loading documents…" : `KYC Documents — ${vReq?.breeder?.name ?? "Breeder"}`}
          </DialogTitle>
          <DialogDescription>
            Review uploaded identification, pet shop licenses, and KYC certificates for trust verification.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {isLoading ? (
            <div className="grid gap-3 sm:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-44 w-full rounded-xl" />
              ))}
            </div>
          ) : error ? (
            <p className="py-8 text-center text-sm text-destructive">
              {error instanceof Error ? error.message : "Could not load verification documents"}
            </p>
          ) : vReq ? (
            <>
              {/* Breeder Overview Card */}
              <Card className="border-border bg-surface/40">
                <CardContent className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Applicant / Owner</p>
                    <p className="truncate text-sm font-semibold text-foreground">
                      {vReq.breeder?.user?.name || "Unnamed"}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {vReq.breeder?.user?.phone ? `+91 ${vReq.breeder.user.phone}` : vReq.breeder?.user?.email || "—"}
                    </p>
                  </div>

                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Shop / Breeder Name</p>
                    <p className="truncate text-sm font-semibold text-foreground">
                      {vReq.breeder?.name || "—"}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {vReq.breeder?.city || vReq.breeder?.user?.city || "Location not provided"}
                    </p>
                  </div>

                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Submitted At</p>
                    <p className="text-sm font-semibold text-foreground">
                      {formatDate(vReq.submitted_at)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {relativeTime(vReq.submitted_at)}
                    </p>
                  </div>

                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Badge &amp; Status</p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {vReq.breeder_verified || vReq.breeder?.verified ? (
                        <Badge variant="success" className="gap-1 text-[11px]">
                          <BadgeCheck className="h-3 w-3" /> Verified Badge
                        </Badge>
                      ) : vReq.breeder_is_approved || vReq.breeder?.is_approved ? (
                        <Badge variant="default" className="gap-1 text-[11px]">
                          <ShieldCheck className="h-3 w-3" /> Approved Seller
                        </Badge>
                      ) : (
                        <Badge variant="warning" className="text-[11px]">
                          KYC Pending
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Uploaded Documents Grid */}
              <div>
                <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Submitted Identity &amp; Shop Documents ({vReq.documents?.length || 0})
                </p>

                {(!vReq.documents || vReq.documents.length === 0) ? (
                  <p className="rounded-lg border border-dashed border-border py-8 text-center text-sm text-muted-foreground">
                    No documents attached to this verification request.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                    {vReq.documents.map((doc: any) => (
                      <div key={doc.id} className="overflow-hidden rounded-xl border border-border bg-card p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
                            {doc.doc_type?.replace(/_/g, " ")}
                          </span>
                          <Badge variant={doc.status === "approved" ? "success" : "secondary"}>
                            {doc.status}
                          </Badge>
                        </div>
                        {doc.doc_url ? (
                          <a
                            href={doc.doc_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group block overflow-hidden rounded-lg border border-border"
                          >
                            <img
                              src={doc.doc_url}
                              alt={doc.doc_type}
                              className="h-44 w-full object-cover transition-transform group-hover:scale-105"
                            />
                          </a>
                        ) : (
                          <div className="flex h-44 w-full items-center justify-center rounded-lg bg-surface text-muted-foreground">
                            <FileText className="h-8 w-8" />
                          </div>
                        )}
                        <div className="mt-2 flex items-center justify-end">
                          <a
                            href={doc.doc_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                          >
                            <ExternalLink className="h-3 w-3" /> Open Full Image
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Review Notes / Rejection Reason */}
              {(vReq.admin_notes || vReq.rejection_reason) && (
                <>
                  <Separator />
                  <div className="space-y-1.5 text-xs">
                    {vReq.admin_notes && (
                      <p className="text-muted-foreground">
                        <strong className="text-foreground">Admin Notes:</strong> {vReq.admin_notes}
                      </p>
                    )}
                    {vReq.rejection_reason && (
                      <p className="text-destructive">
                        <strong>Rejection Reason:</strong> {vReq.rejection_reason}
                      </p>
                    )}
                  </div>
                </>
              )}
            </>
          ) : null}
        </DialogBody>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>

          {vReq?.status === "pending" && (
            <>
              {onReject && (
                <Button
                  variant="destructive"
                  onClick={() => {
                    onOpenChange(false);
                    onReject(vReq.id);
                  }}
                  className="gap-1.5"
                >
                  <XCircle className="h-4 w-4" /> Reject KYC
                </Button>
              )}
              {onApproveOnly && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    onOpenChange(false);
                    onApproveOnly(vReq.id);
                  }}
                  className="gap-1.5"
                >
                  <CheckCircle2 className="h-4 w-4" /> Approve Profile Only
                </Button>
              )}
              {onApproveVerify && (
                <Button
                  variant="success"
                  onClick={() => {
                    onOpenChange(false);
                    onApproveVerify(vReq.id);
                  }}
                  className="gap-1.5"
                >
                  <BadgeCheck className="h-4 w-4" /> Approve &amp; Verify (Badge)
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

